/**
 * Various utils to manipulate lists
 */
export class Lists {
    /**
     * Creates a shallow copy of the array. All elements that are not undefined/null will be in the new list
     */
    public static noNull<T>(array: ReadonlyArray<T> | undefined): T[] | undefined
    public static noNull(array: undefined): undefined
    public static noNull<T>(array: ReadonlyArray<T>): T[]
    public static noNull<T>(array: ReadonlyArray<T>): NonNullable<T>[] {
        return <NonNullable<T>[]>(<unknown>array?.filter((o) => o !== undefined && o !== null))
    }

    /**
     * Removes all empty strings from this list
     * If undefined or null is given, an empty list is returned
     *
     * Lists.noEmpty(undefined) // => []
     * Lists.noEmpty(["abc","","def", null]) // => ["abc","def", null]
     *
     */
    public static noEmpty(array: undefined | null): string[]
    public static noEmpty(array: undefined[]): undefined[]
    public static noEmpty(array: (string | "")[]): Exclude<string, "">[]
    public static noEmpty(array: (string | undefined | null | "")[]): (string | undefined | null)[]
    public static noEmpty(array: string[]): string[] {
        const ls: string[] = []
        if (!array) {
            return ls
        }
        for (const t of array) {
            if (t === "") {
                continue
            }
            ls.push(t)
        }
        return ls
    }

    /**
     * Creates a new array with all elements from 'arr' in such a way that every element will be kept only once
     * Elements are returned in the same order as they appear in the lists.
     * Null/Undefined is returned as is. If an empty array is given, a new empty array will be returned
     */
    public static dedup(arr: NonNullable<ReadonlyArray<string>>): NonNullable<string[]>
    public static dedup(arr: undefined): undefined
    public static dedup(arr: ReadonlyArray<string> | undefined): string[] | undefined
    public static dedup(arr: ReadonlyArray<string>): string[] {
        if (arr === undefined) {
            return undefined
        }
        if (arr === null) {
            return null
        }
        const newArr = []
        for (const string of arr) {
            if (newArr.indexOf(string) < 0) {
                newArr.push(string)
            }
        }
        return newArr
    }

    public static dedupT<T>(arr: ReadonlyArray<T>): T[]
    public static dedupT(arr: null): null
    public static dedupT(arr: undefined): undefined
    public static dedupT<T>(arr: ReadonlyArray<T>): T[] {
        if (arr === undefined) {
            return undefined
        }
        if (arr === null) {
            return null
        }
        const items = []
        for (const item of arr) {
            if (items.indexOf(item) < 0) {
                items.push(item)
            }
        }
        return items
    }

    public static noNullInplace<T>(items: T[]): T[] {
        for (let i = items.length - 1; i >= 0; i--) {
            if (items[i] === null || items[i] === undefined || items[i] === "") {
                items.splice(i, 1)
            }
        }
        return items
    }

    public static sum(list: number[]): number {
        let total = 0
        for (const number of list) {
            total += number
        }
        return total
    }

    /**
     * In the given list, all values which are lists will be merged with the values, e.g.
     *
     * Lists.flatten([ [1,2], 3, [4, [5 ,6]] ]) // => [1, 2, 3, 4, [5, 6]]
     */
    public static flatten<T>(list: (T | T[])[]): T[] {
        const result = []
        for (const value of list) {
            if (Array.isArray(value)) {
                result.push(...value)
            } else {
                result.push(value)
            }
        }
        return result
    }

    /**
     * Deduplicates the given array based on some ID-properties.
     * Removes all falsey values
     * @param arr
     * @param toKey
     * @constructor
     */
    public static dedupOnId<T = { id: string }>(
        arr: T[],
        toKey?: (t: T) => string | string[]
    ): T[] {
        const uniq: T[] = []
        const seen = new Set<string>()
        if (toKey === undefined) {
            toKey = (item) => item["id"]
        }
        for (const img of arr) {
            if (!img) {
                continue
            }
            const ks = toKey(img)
            if (typeof ks === "string") {
                if (!seen.has(ks)) {
                    seen.add(ks)
                    uniq.push(img)
                }
            } else if (ks) {
                const ksNoNull = Lists.noNull(ks)
                const hasBeenSeen = ksNoNull.some((k) => seen.has(k))
                if (!hasBeenSeen) {
                    uniq.push(img)
                }
                for (const k of ksNoNull) {
                    seen.add(k)
                }
            }
        }
        return uniq
    }

    /**
     * Finds all duplicates in a list of strings
     *
     * Lists.duplicates(["a", "b", "c"]) // => []
     * Lists.duplicates(["a", "b","c","b"] // => ["b"]
     * Lists.duplicates(["a", "b","c","b","b"] // => ["b"]
     *
     */
    public static duplicates(arr: ReadonlyArray<string>): string[] {
        if (arr === undefined) {
            return undefined
        }
        const seen = new Set<string>()
        const duplicates = new Set<string>()
        for (const string of arr) {
            if (seen.has(string)) {
                duplicates.add(string)
            }
            seen.add(string)
        }
        return Array.from(duplicates)
    }

    /**
     * Returns true if the contents of `a` are the same (and in the same order) as `b`.
     * Might have false negatives in some cases
     * @param a
     * @param b
     */
    public static sameList<T>(a: ReadonlyArray<T>, b: ReadonlyArray<T>): boolean {
        if (a == b) {
            return true
        }
        if (a === undefined || a === null || b === undefined || b === null) {
            return false
        }
        if (a.length !== b.length) {
            return false
        }
        for (let i = 0; i < a.length; i++) {
            const ai = a[i]
            const bi = b[i]
            if (ai == bi) {
                continue
            }
            if (ai === bi) {
                continue
            }
            return false
        }
        return true
    }
}
